import React, { useEffect, useState } from "react";
import { api, API_BASE, tokenStore } from "../lib/api";
import { PageHeader, KPICard, Empty } from "../components/Bits";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { OUTCOME_COLORS, OUTCOME_LABELS, SENTIMENT_COLORS, formatDuration } from "../lib/format";
import { DownloadSimpleIcon, TrophyIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function Results() {
    const [overview, setOverview] = useState(null);
    const [perf, setPerf] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [a, b] = await Promise.all([
                    api.get("/dashboard/overview"),
                    api.get("/analytics/script-performance"),
                ]);
                setOverview(a.data);
                setPerf(b.data.scripts || []);
            } finally { setLoading(false); }
        };
        load();
    }, []);

    const kpis = overview?.kpis || {};
    const breakdown = overview?.outcome_breakdown || {};
    const breakdownData = Object.entries(breakdown).map(([k, v]) => ({
        name: OUTCOME_LABELS[k] || k,
        key: k,
        value: v,
        color: OUTCOME_COLORS[k] || "#111827",
    })).sort((a, b) => b.value - a.value);

    const topScript = [...perf].sort((a, b) => b.conversion_rate - a.conversion_rate)[0];
    const maxConv = Math.max(...perf.map((p) => p.conversion_rate || 0), 1);

    const exportCsv = () => {
        const t = tokenStore.get();
        fetch(`${API_BASE}/export/calls`, { headers: { Authorization: `Bearer ${t}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "calls.csv";
                a.click();
            });
    };

    const sentimentPct = (s) => {
        // map -1..1 → 0..100 for a bar
        return Math.round(((Number(s) || 0) + 1) * 50);
    };

    return (
        <div className="pb-12">
            <PageHeader
                overline="REPORTING"
                title="Results & analytics"
                description="Outcomes, conversion rates, and script comparison across all campaigns."
                testid="results-header"
                actions={
                    <button type="button" onClick={exportCsv} className="btn-ghost px-4 py-2.5 text-[13px] flex items-center gap-2" data-testid="export-results-button">
                        <DownloadSimpleIcon size={14} weight="bold" /> Export calls CSV
                    </button>
                }
            />

            <div className="px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="results-kpis">
                <KPICard label="TOTAL CALLS" value={kpis.total_calls ?? 0} testid="results-kpi-calls" sub={`${kpis.appointments_booked ?? 0} booked`} />
                <KPICard label="CONV RATE" value={`${kpis.conversion_rate ?? 0}%`} accent testid="results-kpi-conv" sub={`${kpis.whitepapers_sent ?? 0} whitepapers · ${kpis.interested ?? 0} interested`} />
                <KPICard label="AVG DURATION" value={formatDuration(kpis.avg_duration_sec || 0)} testid="results-kpi-duration" sub="per call" />
                <KPICard label="AVG SENTIMENT" value={(kpis.avg_sentiment ?? 0).toFixed(2)} testid="results-kpi-sentiment" sub="range -1 .. +1" />
            </div>

            {/* Top performing script — hero call-out */}
            {topScript && (
                <div className="px-8 mb-3">
                    <div className="swiss-section p-5 flex items-center gap-4 border-l-[3px] border-l-[var(--success)]" data-testid="results-top-script">
                        <div className="w-10 h-10 bg-[var(--success)] text-white flex items-center justify-center" aria-hidden="true">
                            <TrophyIcon size={20} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="overline text-[var(--success)]">TOP PERFORMING SCRIPT</div>
                            <div className="font-bold text-[15px] truncate mt-0.5">{topScript.script_name}</div>
                            <div className="text-[12px] text-[var(--text-secondary)] mono mt-1">
                                {topScript.conversion_rate}% conversion · {topScript.total_calls} calls · {topScript.appointments} booked · avg sentiment {topScript.avg_sentiment}
                            </div>
                        </div>
                        <Link to="/scripts" className="btn-ghost px-3 py-2 text-[12px] flex items-center gap-1.5 shrink-0" data-testid="results-go-scripts">
                            View scripts <ArrowRightIcon size={12} weight="bold" />
                        </Link>
                    </div>
                </div>
            )}

            <div className="px-8 grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="swiss-section p-5 lg:col-span-2" data-testid="results-outcome-chart">
                    <div className="overline">OUTCOMES</div>
                    <h3 className="text-xl font-semibold mt-0.5 mb-1">Distribution by outcome</h3>
                    <p className="text-[12px] text-[var(--text-secondary)] mb-3">Tooltip on hover. Sorted by frequency.</p>
                    {breakdownData.length === 0 ? (
                        <Empty title="No data yet" description="Run a campaign batch to populate reporting." />
                    ) : (
                        <div style={{ height: 320 }}>
                            <ResponsiveContainer>
                                <BarChart data={breakdownData} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid stroke="#E5E7EB" strokeDasharray="0" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#4B5563" allowDecimals={false} />
                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} stroke="#4B5563" width={110} />
                                    <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #111827", fontFamily: "IBM Plex Mono", fontSize: 12 }} cursor={{ fill: "#F3F4F6" }} />
                                    <Bar dataKey="value" isAnimationActive={false} radius={[0, 0, 0, 0]}>
                                        {breakdownData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                {/* Outcomes summary list */}
                <div className="swiss-section p-5" data-testid="results-outcome-summary">
                    <div className="overline">SUMMARY</div>
                    <h3 className="text-xl font-semibold mt-0.5 mb-3">By outcome</h3>
                    {breakdownData.length === 0 ? (
                        <p className="text-[13px] text-[var(--text-secondary)]">No data yet.</p>
                    ) : (
                        <ul className="space-y-2.5" data-testid="results-outcome-list">
                            {breakdownData.map((d) => {
                                const pct = kpis.total_calls ? Math.round((d.value / kpis.total_calls) * 100) : 0;
                                return (
                                    <li key={d.key} className="space-y-1" data-testid={`results-outcome-row-${d.key}`}>
                                        <div className="flex items-center justify-between text-[12px]">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2 h-2" style={{ background: d.color }} aria-hidden="true" />
                                                <span className="font-semibold">{d.name}</span>
                                            </div>
                                            <span className="mono">{d.value} <span className="text-[var(--text-secondary)]">· {pct}%</span></span>
                                        </div>
                                        <div className="h-1 bg-[var(--surface-active)]">
                                            <div className="h-full transition-all duration-500" style={{ background: d.color, width: `${pct}%` }} />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            {/* Script performance — richer table */}
            <div className="px-8 mt-3">
                <div className="swiss-section" data-testid="results-script-perf">
                    <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="overline">SCRIPT PERFORMANCE</div>
                            <h3 className="text-xl font-semibold mt-0.5">Conversion by script</h3>
                        </div>
                        <span className="overline text-[var(--text-secondary)]">{perf.length} {perf.length === 1 ? "SCRIPT" : "SCRIPTS"}</span>
                    </div>
                    {loading ? (
                        <div className="p-10 text-center text-[var(--text-secondary)] text-[13px]">Loading…</div>
                    ) : perf.length === 0 ? (
                        <Empty title="No script data" description="Once campaigns run, this compares scripts side-by-side." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-[13px] min-w-[860px]" data-testid="script-perf-table">
                                <thead>
                                    <tr className="border-b border-[var(--border)] [&>th]:py-3 [&>th]:px-4 [&>th]:overline [&>th]:text-[10px] [&>th]:text-left">
                                        <th>Script</th>
                                        <th className="text-right">Calls</th>
                                        <th className="text-right">Booked</th>
                                        <th>Conversion</th>
                                        <th className="text-right">Avg dur</th>
                                        <th>Sentiment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perf.map((p) => {
                                        const convWidth = maxConv > 0 ? Math.round((p.conversion_rate / maxConv) * 100) : 0;
                                        const sentColor = p.avg_sentiment > 0.15 ? SENTIMENT_COLORS.positive : (p.avg_sentiment < -0.15 ? SENTIMENT_COLORS.negative : SENTIMENT_COLORS.neutral);
                                        return (
                                            <tr key={p.script_id} className="border-b border-[var(--border)] hover:bg-[var(--surface-active)] last:border-b-0" data-testid={`script-perf-row-${p.script_id}`}>
                                                <td className="py-3 px-4 max-w-[260px]">
                                                    <div className="font-semibold truncate">{p.script_name}</div>
                                                    <div className="overline text-[9px] mt-0.5">{p.category}</div>
                                                </td>
                                                <td className="py-3 px-4 mono text-right whitespace-nowrap">{p.total_calls}</td>
                                                <td className="py-3 px-4 mono text-right text-[var(--success)] whitespace-nowrap">{p.appointments}</td>
                                                <td className="py-3 px-4 min-w-[200px]">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-1.5 bg-[var(--surface-active)] min-w-[80px]">
                                                            <div className="h-full bg-[var(--accent)] transition-all duration-500" style={{ width: `${convWidth}%` }} />
                                                        </div>
                                                        <span className="mono text-[12px] font-semibold w-12 text-right">{p.conversion_rate}%</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4 mono text-right whitespace-nowrap">{formatDuration(p.avg_duration)}</td>
                                                <td className="py-3 px-4 min-w-[140px]">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-1.5 bg-[var(--surface-active)] relative min-w-[60px]">
                                                            <div
                                                                className="absolute top-0 h-full transition-all duration-500"
                                                                style={{
                                                                    background: sentColor,
                                                                    left: p.avg_sentiment >= 0 ? "50%" : `${sentimentPct(p.avg_sentiment)}%`,
                                                                    width: `${Math.abs(p.avg_sentiment) * 50}%`,
                                                                }}
                                                            />
                                                            <div className="absolute top-0 left-1/2 w-px h-full bg-[var(--border-strong)]" aria-hidden="true" />
                                                        </div>
                                                        <span className="mono text-[12px] w-10 text-right" style={{ color: sentColor }}>{p.avg_sentiment.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
